import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { AlertTriangle, Wrench, Loader2, ChevronsUpDown, Check, ArrowRight, RefreshCw, Zap, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const API = "/api";

export default function AdminScheduleRepair() {
  const { getAuthHeader } = useAuth();
  const [items, setItems] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reassignFor, setReassignFor] = useState(null); // selected orphan row
  const [newTutorId, setNewTutorId] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const [cleaningAll, setCleaningAll] = useState(false);
  const [deletingTutorId, setDeletingTutorId] = useState(null);

  const auth = { headers: getAuthHeader() };

  const refresh = async () => {
    setLoading(true);
    try {
      const [orphans, tutorList] = await Promise.all([
        axios.get(`${API}/schedule/admin/orphan-slots`, auth),
        axios.get(`${API}/schedule/tutors-list`, auth),
      ]);
      setItems(orphans.data.items || []);
      setTutors(tutorList.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load diagnostics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openReassign = (row) => {
    setReassignFor(row);
    setNewTutorId("");
  };

  const submitReassign = async () => {
    if (!reassignFor || !newTutorId) {
      toast.error("Pick the correct tutor account");
      return;
    }
    if (newTutorId === reassignFor.tutor_id) {
      toast.error("Old and new tutor must be different");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/schedule/admin/reassign-slots`,
        {
          old_tutor_id: reassignFor.tutor_id,
          new_tutor_id: newTutorId,
          update_tutor_name: true,
        },
        auth
      );
      toast.success(
        `Reassigned ${r.data.slots_reassigned} slot(s) to ${r.data.new_tutor.name}`
      );
      setReassignFor(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reassign failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSyncAll = async () => {
    if (!window.confirm(
      "This will scan every tutor profile and migrate any legacy schedule_slots " +
      "(matched by tutor name) to the canonical user account.\n\nProceed?"
    )) return;
    setSyncingAll(true);
    try {
      const r = await axios.post(`${API}/schedule/admin/sync-all-tutors`, {}, auth);
      const total = r.data.total_slots_synced || 0;
      const changed = r.data.profiles_with_changes || [];
      if (total === 0) {
        toast.success(`All ${r.data.profiles_scanned} profile(s) already in sync ✓`);
      } else {
        toast.success(
          `Synced ${total} slot(s) across ${changed.length} tutor(s)`
        );
      }
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncingAll(false);
    }
  };

  const handleCleanupEmpty = async () => {
    if (!window.confirm(
      "This will DELETE all orphan tutor_ids that have ZERO active bookings " +
      "(just empty availability templates from deleted accounts).\n\n" +
      "Active student bookings will NOT be touched — those still need manual reassignment.\n\n" +
      "Proceed?"
    )) return;
    setCleaningAll(true);
    try {
      const r = await axios.post(`${API}/schedule/admin/cleanup-empty-orphans`, {}, auth);
      const deleted = r.data.total_deleted || 0;
      const skipped = r.data.groups_skipped_active || 0;
      if (deleted === 0 && skipped === 0) {
        toast.success("No empty orphan slots found ✓");
      } else {
        toast.success(
          `Deleted ${deleted} empty slot(s) from ${r.data.groups_cleaned.length} orphan group(s).` +
          (skipped ? ` ${skipped} group(s) skipped (still have active bookings).` : "")
        );
      }
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed");
    } finally {
      setCleaningAll(false);
    }
  };

  const handleDeleteRow = async (row) => {
    const label = row.linked_user?.name || row.slot_tutor_names?.[0] || row.tutor_id.slice(0, 8);
    if (row.active_slots > 0) {
      if (!window.confirm(
        `⚠️ This tutor has ${row.active_slots} ACTIVE booking(s). ` +
        `Deleting will LOSE that data permanently. Reassigning is usually safer.\n\n` +
        `Force delete all ${row.total_slots} slot(s) for "${label}"?`
      )) return;
    } else {
      if (!window.confirm(
        `Delete ${row.total_slots} empty slot(s) for "${label}"?\n` +
        `(No active student bookings will be lost.)`
      )) return;
    }
    setDeletingTutorId(row.tutor_id);
    try {
      const force = row.active_slots > 0;
      const r = await axios.delete(
        `${API}/schedule/admin/orphan-slots/${row.tutor_id}${force ? "?force=true" : ""}`,
        auth
      );
      toast.success(`Deleted ${r.data.deleted} slot(s)`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeletingTutorId(null);
    }
  };

  const orphanCount = items.filter((i) => i.is_orphan).length;
  const totalOrphanSlots = items
    .filter((i) => i.is_orphan)
    .reduce((s, i) => s + (i.total_slots || 0), 0);

  return (
    <div className="space-y-6" data-testid="admin-schedule-repair-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" data-testid="schedule-repair-title">
            Schedule Repair Tool
          </h1>
          <p className="text-slate-500 text-sm mt-1 max-w-2xl">
            Diagnose and fix bookings that aren't appearing on a tutor's dashboard.
            Usually happens when a tutor profile was relinked to a new user account
            after bookings already existed.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={handleSyncAll}
            disabled={syncingAll || loading}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="sync-all-btn"
          >
            {syncingAll ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Zap className="w-4 h-4 mr-2" />
            )}
            Auto-Sync All Tutors
          </Button>
          <Button
            onClick={handleCleanupEmpty}
            disabled={cleaningAll || loading}
            variant="outline"
            className="border-rose-300 text-rose-700 hover:bg-rose-50"
            data-testid="cleanup-empty-btn"
          >
            {cleaningAll ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            Clear All Empty Orphans
          </Button>
          <Button variant="outline" onClick={refresh} disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} /> Refresh
          </Button>
        </div>
      </div>

      <Card className="border-emerald-200 bg-emerald-50/50">
        <CardContent className="pt-5 pb-5 text-sm text-emerald-900 flex items-start gap-3">
          <Zap className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
          <div>
            <strong>Universal Auto-Sync</strong> — Click <em>Auto-Sync All Tutors</em> to scan every
            profile and migrate any legacy bookings (matched by tutor name) to the canonical user
            account. This is safe to run anytime: it's idempotent and only updates slots whose
            <code className="px-1 mx-0.5 bg-white rounded text-xs">tutor_id</code> or
            <code className="px-1 mx-0.5 bg-white rounded text-xs">tutor_name</code> doesn't already
            match the profile's linked user.
          </div>
        </CardContent>
      </Card>

      {!loading && orphanCount > 0 && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="pt-6 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-sm text-amber-900">
              <strong>{orphanCount} orphan tutor ID{orphanCount === 1 ? "" : "s"}</strong> detected,
              affecting <strong>{totalOrphanSlots} slot{totalOrphanSlots === 1 ? "" : "s"}</strong>.
              These bookings will not render on any tutor's dashboard until reassigned.
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-slate-500">
            No schedule slots in the system yet.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((row) => (
            <Card
              key={row.tutor_id}
              className={cn(
                "transition-shadow",
                row.is_orphan ? "border-amber-300" : "border-slate-200"
              )}
              data-testid={`row-${row.tutor_id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                      {row.linked_user ? (
                        <>
                          <span>{row.linked_user.name}</span>
                          <Badge variant="outline" className="text-xs">
                            {row.linked_user.role}
                          </Badge>
                          <span className="text-xs text-slate-500">{row.linked_user.email}</span>
                        </>
                      ) : (
                        <>
                          <span className="text-amber-700">Unknown user</span>
                          {row.slot_tutor_names?.length > 0 && (
                            <span className="text-sm font-normal text-slate-700">
                              — stored name:{" "}
                              <strong>{row.slot_tutor_names.join(" / ")}</strong>
                            </span>
                          )}
                        </>
                      )}
                      {row.is_orphan && (
                        <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100 border-amber-300">
                          Orphaned
                        </Badge>
                      )}
                      {row.active_slots === 0 && row.is_orphan && (
                        <Badge variant="outline" className="text-xs text-rose-700 border-rose-300">
                          Empty (safe to delete)
                        </Badge>
                      )}
                    </CardTitle>
                    <div className="text-xs text-slate-500 mt-1 font-mono">
                      tutor_id: {row.tutor_id}
                    </div>
                    {row.linked_profiles?.length > 0 && (
                      <div className="text-xs text-slate-600 mt-1">
                        Linked profile{row.linked_profiles.length === 1 ? "" : "s"}:{" "}
                        {row.linked_profiles.map((p) => p.name).join(", ")}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-xs text-slate-500">Total / Active slots</div>
                      <div className="text-sm font-semibold">
                        {row.total_slots} / {row.active_slots}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant={row.is_orphan ? "default" : "outline"}
                      onClick={() => openReassign(row)}
                      data-testid={`reassign-${row.tutor_id}`}
                    >
                      <Wrench className="w-4 h-4 mr-2" /> Reassign
                    </Button>
                    {row.is_orphan && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-rose-300 text-rose-700 hover:bg-rose-50"
                        onClick={() => handleDeleteRow(row)}
                        disabled={deletingTutorId === row.tutor_id}
                        data-testid={`delete-${row.tutor_id}`}
                      >
                        {deletingTutorId === row.tutor_id ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4 mr-2" />
                        )}
                        {row.active_slots > 0 ? "Force Delete" : "Delete"}
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              {row.sample?.length > 0 && (
                <CardContent className="pt-0">
                  <div className="text-xs text-slate-500 mb-1">Recent slots:</div>
                  <div className="space-y-1">
                    {row.sample.map((s) => {
                      const isActive = !["available", "cancelled", "cancelled_by_student", "cancelled_by_teacher"].includes(s.status);
                      return (
                        <div
                          key={s.id}
                          className={cn(
                            "text-xs flex items-center gap-2 rounded px-1.5 py-0.5",
                            isActive ? "bg-amber-50 text-amber-900" : "text-slate-700"
                          )}
                        >
                          <span className="font-mono w-32">{s.date} {s.time}</span>
                          <span className="flex-1 truncate">
                            {s.student_name ? (
                              <strong>{s.student_name}</strong>
                            ) : (
                              <em className="text-slate-400">(available)</em>
                            )}
                          </span>
                          <Badge variant="outline" className="text-[10px]">{s.status}</Badge>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Reassign dialog */}
      <Dialog open={!!reassignFor} onOpenChange={(o) => !o && setReassignFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reassign Schedule Slots</DialogTitle>
            <DialogDescription>
              Move all slots from this tutor_id to the correct tutor account. This will make them
              show up on that tutor's <code>/schedule</code> dashboard.
            </DialogDescription>
          </DialogHeader>

          {reassignFor && (
            <div className="space-y-3 py-2">
              <div className="text-sm space-y-1 p-3 rounded-md bg-slate-50 border border-slate-200">
                <div>
                  <span className="text-slate-500">From:</span>{" "}
                  <span className="font-medium">
                    {reassignFor.linked_user?.name || "Unknown"}
                  </span>
                </div>
                <div className="font-mono text-xs text-slate-500 break-all">
                  {reassignFor.tutor_id}
                </div>
                <div className="text-xs text-slate-600 mt-1">
                  {reassignFor.total_slots} slot{reassignFor.total_slots === 1 ? "" : "s"} will be moved.
                </div>
              </div>

              <div className="flex justify-center">
                <ArrowRight className="w-5 h-5 text-slate-400" />
              </div>

              <div>
                <div className="text-xs text-slate-500 mb-1">Correct tutor account:</div>
                <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={pickerOpen}
                      className="w-full justify-between font-normal"
                      data-testid="reassign-tutor-trigger"
                    >
                      <span className="truncate text-left">
                        {newTutorId
                          ? (() => {
                              const t = tutors.find((x) => x.id === newTutorId);
                              return t ? `${t.name} (${t.email})` : "— Pick a tutor —";
                            })()
                          : "— Pick a tutor —"}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[--radix-popover-trigger-width] p-0"
                    align="start"
                  >
                    <Command>
                      <CommandInput placeholder="Search tutor..." data-testid="reassign-tutor-search" />
                      <CommandList>
                        <CommandEmpty>No tutor found.</CommandEmpty>
                        <CommandGroup>
                          {tutors.map((t) => (
                            <CommandItem
                              key={t.id}
                              value={`${t.name} ${t.email}`}
                              onSelect={() => {
                                setNewTutorId(t.id);
                                setPickerOpen(false);
                              }}
                              data-testid={`reassign-tutor-option-${t.id}`}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  newTutorId === t.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <div className="flex flex-col">
                                <span className="font-medium">{t.name}</span>
                                <span className="text-xs text-slate-500">{t.email}</span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setReassignFor(null)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={submitReassign}
              disabled={submitting || !newTutorId}
              data-testid="confirm-reassign-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Reassign Slots
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
